import { useEffect, useRef, useState } from "react";
import { ReactComponent as GleapBotIcon } from "../../Assets/GleapBotIcon.svg";
import { AvatarImage } from "../../Components/AvatarImage/AvatarImage";
import { KaiAgentStatus } from "../../Components/KaiAgentStatus/KaiAgentStatus";
import { getAiAssistantLabel } from "../../Helper/AiDisclosure";
import { captionChunks, captionSentence } from "../../Helper/KaiVoiceCaption";
import { useConfigStore } from "../../Stores/Config";
import { useKaiVoiceStore } from "../../Stores/KaiVoice";
import { KaiVoiceCaptionStage } from "./KaiVoiceCaptionStage";
import { KaiVoiceLevelBars } from "./KaiVoiceLevelBars";
import { KaiVoiceParticles } from "./KaiVoiceParticles";
import "./KaiVoiceCall.scss";

const formatElapsed = (elapsedMs: number): string => {
  const totalSeconds = Math.max(Math.floor(elapsedMs / 1000), 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const MicIcon = ({ muted }: { muted: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" x2="12" y1="19" y2="22" />
    {muted && <line x1="3" x2="21" y1="3" y2="21" />}
  </svg>
);

const MusicIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const EndCallIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1 0-1.41C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85a.996.996 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9Z" />
  </svg>
);

/**
 * Full-screen call surface, swapped in place of the conversation body while a
 * Kai Voice call runs (same early-return pattern the VideoCall uses). Always
 * dark — the palette is scoped in KaiVoiceCall.scss, independent of the
 * merchant theme, matching the Fin-style call aesthetic.
 */
const KaiVoiceCall = () => {
  const configStore = useConfigStore();
  const callState = useKaiVoiceStore((state) => state.callState);
  const mode = useKaiVoiceStore((state) => state.mode);
  const errorType = useKaiVoiceStore((state) => state.errorType);
  const muted = useKaiVoiceStore((state) => state.muted);
  const startedAtMs = useKaiVoiceStore((state) => state.startedAtMs);
  const maxDurationSec = useKaiVoiceStore((state) => state.maxDurationSec);
  const captionText = useKaiVoiceStore((state) => state.captionText);
  const captionTurn = useKaiVoiceStore((state) => state.captionTurn);
  const captionSpokenWords = useKaiVoiceStore((state) => state.captionSpokenWords);
  const captionChunkEnds = useKaiVoiceStore((state) => state.captionChunkEnds);
  const onHold = useKaiVoiceStore((state) => state.onHold);
  const toggleMute = useKaiVoiceStore((state) => state.toggleMute);
  const endCall = useKaiVoiceStore((state) => state.endCall);
  const dismiss = useKaiVoiceStore((state) => state.dismiss);
  const getAudioLevel = useKaiVoiceStore((state) => state.getAudioLevel);
  // Harness flag: freezes timers + canvas animation so VR runs stay pixel-identical.
  const previewMode = useKaiVoiceStore((state) => state.previewMode);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  const operatorName = configStore.getBotName();
  const operatorAvatarImage = configStore.config?.operatorAvatarImage;
  // The sentence Kai is saying, word by word as it is spoken; the rest of a
  // long answer is in the thread.
  const caption = captionSentence(captionText, captionSpokenWords, captionChunkEnds);
  const sentenceKey = `${captionTurn}-${caption.index}`;

  const [elapsedMs, setElapsedMs] = useState(() => (startedAtMs ? Date.now() - startedAtMs : 0));
  const startedAtRef = useRef(startedAtMs);
  startedAtRef.current = startedAtMs;

  useEffect(() => {
    if (callState !== "active" || previewMode) {
      return;
    }
    const interval = setInterval(() => {
      if (startedAtRef.current) {
        setElapsedMs(Date.now() - startedAtRef.current);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [callState, previewMode]);

  // Auto-leave the ended screen back into the conversation (the transcript is
  // already in the thread behind us).
  useEffect(() => {
    if (callState !== "ended" || previewMode) {
      return;
    }
    const timeout = setTimeout(() => {
      dismiss();
    }, 1400);
    return () => clearTimeout(timeout);
  }, [callState, dismiss, previewMode]);

  // The overlay is position:absolute inside .main-app — a scrolled home page
  // would otherwise leave it offset by the scroll distance.
  useEffect(() => {
    try {
      document.querySelector(".main-app")?.scrollTo?.(0, 0);
    } catch (_) {}
  }, []);

  // Dialog semantics: focus the primary action on mount AND whenever the body
  // swaps (End → Close on the ended/error screens), Esc hangs up / dismisses.
  const bodyKind = callState === "error" || callState === "ended" ? "final" : callState === "active" ? "active" : "pending";
  useEffect(() => {
    primaryButtonRef.current?.focus({ preventScroll: true });
  }, [callState]);

  const callStateRef = useRef(callState);
  callStateRef.current = callState;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // App-level Esc closes the whole widget — the call dialog owns it now.
      event.stopPropagation();
      event.preventDefault();
      const current = callStateRef.current;
      if (current === "active" || current === "connecting") {
        endCall();
      } else {
        dismiss();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [endCall, dismiss]);

  const remainingSec = maxDurationSec - Math.floor(elapsedMs / 1000);
  const timerWarning = callState === "active" && remainingSec <= 30;

  const renderError = () => {
    const errorKeyMap: Record<string, { key: string; fallback: string }> = {
      micDenied: {
        key: "voiceMicDenied",
        fallback: "Microphone access was denied. Please allow microphone access to start a voice call.",
      },
      policyBlocked: {
        key: "voiceMicBlockedBySite",
        fallback: "This site blocks microphone access, so voice calls aren't available here.",
      },
      connectFailed: {
        key: "voiceCallFailed",
        fallback: "The call couldn't be connected. Please try again.",
      },
      dropped: {
        key: "voiceCallDropped",
        fallback: "The call was disconnected.",
      },
      busy: {
        key: "voiceCallBusy",
        fallback: "A voice call is already running for this session. Please end it first.",
      },
      rateLimited: {
        key: "voiceCallRateLimited",
        fallback: "Too many call attempts — please wait a moment and try again.",
      },
      unavailable: {
        key: "voiceCallUnavailable",
        fallback: "Voice calls aren't available right now. Please write us a message instead.",
      },
    };
    const copy = errorKeyMap[errorType ?? "connectFailed"];

    return (
      <div className="kai-voice-call--center">
        <div className="kai-voice-call--error-message">
          {configStore.translateTextWithFallback(copy.key, copy.fallback)}
        </div>
        <button
          type="button"
          ref={primaryButtonRef}
          className="kai-voice-call--dismiss"
          onClick={() => dismiss()}
        >
          {configStore.translateTextWithFallback("close", "Close")}
        </button>
      </div>
    );
  };

  const renderActive = () => (
    <>
      <div className="kai-voice-call--captions">
        <KaiVoiceCaptionStage
          sentenceKey={sentenceKey}
          tokens={caption.tokens}
          spoken={caption.spoken}
          instant={previewMode}
        />
        {onHold && (
          <div className="kai-voice-call--hold">
            <MusicIcon />
            <span>{configStore.translateTextWithFallback("voiceOnHold", "On hold")}</span>
          </div>
        )}
      </div>
      <div className="kai-voice-call--controls">
        <KaiVoiceLevelBars getLevel={getAudioLevel} frozen={previewMode} />
        <div className="kai-voice-call--buttons">
          <div className="kai-voice-call--button-group">
            <button
              type="button"
              className={`kai-voice-call--round-button ${muted ? "kai-voice-call--round-button-active" : ""}`}
              aria-pressed={muted}
              aria-label={configStore.translateTextWithFallback(muted ? "voiceUnmute" : "voiceMute", muted ? "Unmute" : "Mute")}
              onClick={() => toggleMute()}
            >
              <MicIcon muted={muted} />
            </button>
            <span className="kai-voice-call--button-label">
              {configStore.translateTextWithFallback(muted ? "voiceUnmute" : "voiceMute", muted ? "Unmute" : "Mute")}
            </span>
          </div>
          <div className="kai-voice-call--button-group">
            <button
              type="button"
              ref={primaryButtonRef}
              className="kai-voice-call--round-button kai-voice-call--round-button-end"
              aria-label={configStore.translateTextWithFallback("voiceEndCall", "End")}
              onClick={() => endCall()}
            >
              <EndCallIcon />
            </button>
            <span className="kai-voice-call--button-label">
              {configStore.translateTextWithFallback("voiceEndCall", "End")}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const renderBody = () => {
    if (callState === "error") {
      return renderError();
    }
    if (callState === "ended") {
      return (
        <div className="kai-voice-call--center">
          <div className="kai-voice-call--status-text">
            {configStore.translateTextWithFallback("voiceCallEnded", "Call ended")}
          </div>
        </div>
      );
    }
    if (callState === "active") {
      return renderActive();
    }
    return (
      <div className="kai-voice-call--center">
        <div className="kai-voice-call--status-text kai-voice-call--status-subtle">
          {configStore.translateTextWithVarsAndFallback(
            "callingKai",
            { operatorName },
            `Calling ${operatorName}…`,
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="kai-voice-call" role="dialog" aria-modal="true" aria-label={`${operatorName} voice call`}>
      <KaiVoiceParticles getLevel={getAudioLevel} thinking={mode === "thinking"} frozen={previewMode} />
      <div className="kai-voice-call--header">
        {/* Who you're talking to: the operator's image if configured, else the
            Kai mark — deliberately static; the particle sphere carries the state. */}
        <div className="kai-voice-call--identity">
          <span className="kai-voice-call--identity-avatar">
            {operatorAvatarImage ? (
              <AvatarImage src={operatorAvatarImage} alt={operatorName} fallback={<GleapBotIcon />} />
            ) : (
              <KaiAgentStatus state="idle" size={26} radius="7px" frozen />
            )}
          </span>
          {configStore.isEuAiActEnforced() ? (
            // "Enforce EU AI Act": the caller is told they talk to an AI.
            <span className="kai-voice-call--identity-text">
              <span className="kai-voice-call--identity-name">{operatorName}</span>
              <span className="kai-voice-call--identity-ai">
                {getAiAssistantLabel()}
              </span>
            </span>
          ) : (
            <span className="kai-voice-call--identity-name">{operatorName}</span>
          )}
        </div>
        <div className="kai-voice-call--header-meta">
        {callState === "active" && (
          <span className={`kai-voice-call--timer ${timerWarning ? "kai-voice-call--timer-warning" : ""}`}>
            {formatElapsed(elapsedMs)}
          </span>
        )}
        <button
          type="button"
          ref={bodyKind === "active" || callState === "error" ? undefined : primaryButtonRef}
          className="kai-voice-call--close"
          aria-label={configStore.translateTextWithFallback("voiceEndCall", "End")}
          onClick={() => {
            if (callState === "active" || callState === "connecting") {
              endCall();
            } else {
              dismiss();
            }
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        </div>
      </div>
      {renderBody()}
      {/* Mounted before the call is active, so the greeting is announced too; each sentence once, not every word. */}
      <div className="kai-voice-call--sr-only" aria-live="polite">
        {callState === "active" &&
          captionChunks(captionText, captionChunkEnds).map((chunk, index) => (
            <span key={`${captionTurn}-${index}`}>{`${chunk} `}</span>
          ))}
      </div>
      <div className="kai-voice-call--sr-only" role="status">
        {callState === "active" && onHold ? configStore.translateTextWithFallback("voiceOnHold", "On hold") : ""}
      </div>
    </div>
  );
};

export default KaiVoiceCall;
